import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  businessDateTimeToDate,
  formatAppointmentDateWithWeekday,
  formatAppointmentTime,
  toBusinessDateKey,
} from "@zapis24/utils";
import { apiFetch } from "../api.js";

interface WorkScheduleDay {
  id?: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isWorking: boolean;
}

interface ScheduleBlock {
  id: string;
  startsAt: string;
  endsAt: string;
  reason: string | null;
}

interface WorkScheduleBreak {
  id: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  label: string | null;
}

const DAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
type ScheduleBlockMode = "time" | "day";
const WORK_TIME_INPUT_CLASS =
  "input h-10 min-w-0 flex-1 px-2 text-center";
const BLOCK_INPUT_CLASS = "input block w-full min-w-0 max-w-full";

interface ScheduleBlockFormState {
  mode: ScheduleBlockMode;
  date: string;
  endDate: string;
  startTime: string;
  endTime: string;
  reason: string;
}

interface ScheduleBreakFormState {
  dayOfWeeks: number[];
  startTime: string;
  endTime: string;
  label: string;
}

interface ScheduleBreakGroup {
  key: string;
  ids: string[];
  dayOfWeeks: number[];
  startTime: string;
  endTime: string;
  label: string | null;
}

function defaultSchedule(): WorkScheduleDay[] {
  return DAY_LABELS.map((_, dayOfWeek) => ({
    dayOfWeek,
    startTime: "09:00",
    endTime: "19:00",
    isWorking: dayOfWeek < 5,
  }));
}

export function ScheduleSettingsSection({ token, timeZone }: { token: string; timeZone?: string }) {
  const qc = useQueryClient();
  const [days, setDays] = useState<WorkScheduleDay[]>(defaultSchedule);
  const [scheduleSaved, setScheduleSaved] = useState(false);
  const [scheduleError, setScheduleError] = useState("");
  const [blockError, setBlockError] = useState("");
  const [breakError, setBreakError] = useState("");
  const [breakForm, setBreakForm] = useState<ScheduleBreakFormState>(() => ({
    dayOfWeeks: [0, 1, 2, 3, 4],
    startTime: "13:00",
    endTime: "14:00",
    label: "Обед",
  }));
  const [blockForm, setBlockForm] = useState<ScheduleBlockFormState>(() => ({
    mode: "time" as ScheduleBlockMode,
    date: toDateInputValue(addDays(new Date(), 1)),
    endDate: toDateInputValue(addDays(new Date(), 1)),
    startTime: "13:00",
    endTime: "15:00",
    reason: "",
  }));

  const { data: schedule = [], isLoading: scheduleLoading } = useQuery({
    queryKey: ["schedule"],
    queryFn: () => apiFetch<WorkScheduleDay[]>(token, "/api/schedule"),
  });

  useEffect(() => {
    if (schedule.length === 0) return;
    const byDay = new Map(schedule.map((day) => [day.dayOfWeek, day]));
    setDays(defaultSchedule().map((day) => byDay.get(day.dayOfWeek) ?? day));
  }, [schedule]);

  const blockRange = {
    from: toDateInputValue(new Date()),
    to: toDateInputValue(addDays(new Date(), 60)),
  };

  const { data: blocks = [], isLoading: blocksLoading } = useQuery({
    queryKey: ["schedule-blocks", blockRange.from, blockRange.to],
    queryFn: () =>
      apiFetch<ScheduleBlock[]>(
        token,
        `/api/schedule/blocks?from=${blockRange.from}&to=${blockRange.to}`,
      ),
  });

  const { data: scheduleBreaks = [], isLoading: breaksLoading } = useQuery({
    queryKey: ["schedule-breaks"],
    queryFn: () => apiFetch<WorkScheduleBreak[]>(token, "/api/schedule/breaks"),
  });
  const groupedScheduleBreaks = groupScheduleBreaks(scheduleBreaks);

  const saveScheduleMutation = useMutation({
    mutationFn: () =>
      apiFetch<WorkScheduleDay[]>(token, "/api/schedule", {
        method: "PUT",
        body: JSON.stringify({ days }),
      }),
    onSuccess: (updated) => {
      setDays(updated);
      setScheduleError("");
      setScheduleSaved(true);
      setTimeout(() => setScheduleSaved(false), 2500);
      void qc.invalidateQueries({ queryKey: ["schedule"] });
    },
    onError: (error: Error) => setScheduleError(error.message),
  });

  const createBreakMutation = useMutation({
    mutationFn: () => {
      if (breakForm.dayOfWeeks.length === 0) {
        throw new Error("Выберите дни для перерыва");
      }
      if (breakForm.endTime <= breakForm.startTime) {
        throw new Error("Время окончания должно быть позже начала");
      }
      return apiFetch<WorkScheduleBreak[]>(token, "/api/schedule/breaks", {
        method: "POST",
        body: JSON.stringify({
          dayOfWeeks: breakForm.dayOfWeeks,
          startTime: breakForm.startTime,
          endTime: breakForm.endTime,
          label: breakForm.label.trim() || undefined,
        }),
      });
    },
    onSuccess: () => {
      setBreakError("");
      void qc.invalidateQueries({ queryKey: ["schedule-breaks"] });
    },
    onError: (error: Error) => setBreakError(error.message),
  });

  const deleteBreakMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(
        ids.map((id) => apiFetch(token, `/api/schedule/breaks/${id}`, { method: "DELETE" })),
      );
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["schedule-breaks"] }),
  });

  const createBlockMutation = useMutation({
    mutationFn: () => {
      const { startsAt, endsAt } = buildScheduleBlockInterval(blockForm, timeZone);
      if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
        throw new Error(blockForm.mode === "day" ? "Выберите даты закрытия" : "Выберите дату и время");
      }
      if (endsAt <= startsAt) {
        throw new Error(
          blockForm.mode === "day"
            ? "Дата окончания не может быть раньше даты начала"
            : "Время окончания должно быть позже начала",
        );
      }
      return apiFetch<ScheduleBlock>(token, "/api/schedule/blocks", {
        method: "POST",
        body: JSON.stringify({
          startsAt,
          endsAt,
          reason: blockForm.reason.trim() || undefined,
        }),
      });
    },
    onSuccess: () => {
      setBlockError("");
      setBlockForm((current) => ({ ...current, reason: "" }));
      void qc.invalidateQueries({ queryKey: ["schedule-blocks"] });
    },
    onError: (error: Error) => setBlockError(error.message),
  });

  const deleteBlockMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch(token, `/api/schedule/blocks/${id}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["schedule-blocks"] }),
  });

  function updateDay(dayOfWeek: number, patch: Partial<WorkScheduleDay>) {
    setDays((current) =>
      current.map((day) => (day.dayOfWeek === dayOfWeek ? { ...day, ...patch } : day)),
    );
  }

  function toggleBreakDay(dayOfWeek: number) {
    setBreakForm((current) => {
      const isSelected = current.dayOfWeeks.includes(dayOfWeek);
      const dayOfWeeks = isSelected
        ? current.dayOfWeeks.filter((item) => item !== dayOfWeek)
        : [...current.dayOfWeeks, dayOfWeek].sort((a, b) => a - b);

      return { ...current, dayOfWeeks };
    });
  }

  function updateBlockDate(date: string) {
    setBlockForm((current) => ({
      ...current,
      date,
      endDate: current.endDate < date ? date : current.endDate,
    }));
  }

  function updateBlockMode(mode: ScheduleBlockMode) {
    setBlockForm((current) => ({
      ...current,
      mode,
      endDate: current.endDate || current.date,
    }));
  }

  return (
    <SettingsSection title="График работы">
      <div className="space-y-4">
        <div className="rounded-xl bg-gray-50 px-3 py-2 text-xs leading-5 text-gray-500">
          Рабочие дни формируют свободные слоты для клиентов. Если нужно разово закрыть время, добавьте временное закрытие ниже.
        </div>

        <div className="space-y-1.5">
          {days.map((day) => (
            <div
              key={day.dayOfWeek}
              className="grid min-w-0 grid-cols-1 gap-2 rounded-xl border border-gray-100 p-2.5 sm:grid-cols-[44px_96px_1fr] sm:items-center sm:p-3"
            >
              <div className="flex min-w-0 items-center gap-3 sm:contents">
                <div className="w-8 shrink-0 text-sm font-semibold text-gray-700 sm:w-auto sm:pt-2">
                  {DAY_LABELS[day.dayOfWeek]}
                </div>
                <label className="flex min-w-0 items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={day.isWorking}
                    onChange={(event) => updateDay(day.dayOfWeek, { isWorking: event.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  Рабочий
                </label>
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <input
                  type="time"
                  value={day.startTime}
                  disabled={!day.isWorking}
                  onChange={(event) => updateDay(day.dayOfWeek, { startTime: event.target.value })}
                  className={WORK_TIME_INPUT_CLASS}
                  aria-label={`${DAY_LABELS[day.dayOfWeek]} с`}
                />
                <span className="shrink-0 text-sm font-medium text-gray-300" aria-hidden="true">-</span>
                <input
                  type="time"
                  value={day.endTime}
                  disabled={!day.isWorking}
                  onChange={(event) => updateDay(day.dayOfWeek, { endTime: event.target.value })}
                  className={WORK_TIME_INPUT_CLASS}
                  aria-label={`${DAY_LABELS[day.dayOfWeek]} по`}
                />
              </div>
            </div>
          ))}
        </div>

        {scheduleSaved && (
          <p className="rounded-xl bg-green-50 px-3 py-2 text-sm text-green-700">График сохранён</p>
        )}
        {scheduleError && (
          <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600">{scheduleError}</p>
        )}

        <button
          type="button"
          onClick={() => saveScheduleMutation.mutate()}
          disabled={scheduleLoading || saveScheduleMutation.isPending}
          className="w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
        >
          {saveScheduleMutation.isPending ? "Сохраняем..." : "Сохранить график"}
        </button>

        <div className="border-t border-gray-100 pt-4">
          <h4 className="text-sm font-semibold text-gray-700">Перерывы по дням недели</h4>
          <p className="mt-1 text-xs leading-5 text-gray-500">
            Например: Пн-Пт, 13:00-14:00. Эти слоты будут закрываться каждую неделю.
          </p>

          <div className="mt-3 space-y-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold text-gray-500">Дни</span>
              <div className="grid grid-cols-7 gap-1">
                {DAY_LABELS.map((label, dayOfWeek) => {
                  const selected = breakForm.dayOfWeeks.includes(dayOfWeek);
                  return (
                    <button
                      key={dayOfWeek}
                      type="button"
                      onClick={() => toggleBreakDay(dayOfWeek)}
                      className={`h-9 rounded-lg border text-xs font-semibold transition ${
                        selected
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-gray-100 bg-white text-gray-500 hover:border-gray-200"
                      }`}
                      aria-pressed={selected}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </label>

            <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.6fr)]">
              <label className="min-w-0">
                <span className="mb-1 block text-[11px] font-semibold text-gray-500">С</span>
                <input
                  type="time"
                  value={breakForm.startTime}
                  onChange={(event) => setBreakForm({ ...breakForm, startTime: event.target.value })}
                  className={BLOCK_INPUT_CLASS}
                />
              </label>
              <label className="min-w-0">
                <span className="mb-1 block text-[11px] font-semibold text-gray-500">До</span>
                <input
                  type="time"
                  value={breakForm.endTime}
                  onChange={(event) => setBreakForm({ ...breakForm, endTime: event.target.value })}
                  className={BLOCK_INPUT_CLASS}
                />
              </label>
              <label className="col-span-2 min-w-0 sm:col-span-1">
                <span className="mb-1 block text-[11px] font-semibold text-gray-500">Название</span>
                <input
                  value={breakForm.label}
                  onChange={(event) => setBreakForm({ ...breakForm, label: event.target.value })}
                  className={BLOCK_INPUT_CLASS}
                  placeholder="Обед"
                />
              </label>
            </div>
          </div>

          {breakError && (
            <p className="mt-2 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600">{breakError}</p>
          )}

          <button
            type="button"
            onClick={() => createBreakMutation.mutate()}
            disabled={createBreakMutation.isPending}
            className="mt-3 w-full rounded-xl border border-primary/20 bg-white py-2.5 text-sm font-semibold text-primary transition hover:bg-primary/5 disabled:opacity-50"
          >
            {createBreakMutation.isPending ? "Добавляем..." : "Добавить перерыв"}
          </button>

          <div className="mt-4 space-y-2">
            {breaksLoading && <p className="text-xs text-gray-400">Загружаем перерывы...</p>}
            {!breaksLoading && groupedScheduleBreaks.length === 0 && (
              <p className="rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-400">Постоянных перерывов нет</p>
            )}
            {groupedScheduleBreaks.map((item) => (
              <div
                key={item.key}
                className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-700">
                    {formatBreakDays(item.dayOfWeeks)} · {item.startTime}-{item.endTime}
                  </p>
                  {item.label && <p className="truncate text-xs text-gray-400">{item.label}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => deleteBreakMutation.mutate(item.ids)}
                  disabled={deleteBreakMutation.isPending}
                  className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-red-500 transition hover:bg-red-50 disabled:opacity-50"
                >
                  Удалить
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <h4 className="text-sm font-semibold text-gray-700">Временные закрытия</h4>
          <p className="mt-1 text-xs leading-5 text-gray-500">
            Для разовых дел: выберите дату и время, когда запись закрыта. Время с — начало
            закрытия, время до — когда снова можно записываться.
          </p>

          <div className="mt-3 grid grid-cols-2 rounded-xl bg-gray-50 p-1">
            {(["time", "day"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => updateBlockMode(mode)}
                className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                  blockForm.mode === mode
                    ? "bg-white text-primary shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {mode === "time" ? "Интервал" : "День целиком"}
              </button>
            ))}
          </div>

          {blockForm.mode === "time" ? (
            <div className="mt-3 grid min-w-0 max-w-full gap-2 overflow-hidden sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)]">
              <label className="min-w-0">
                <span className="mb-1 block text-[11px] font-semibold text-gray-500">Дата</span>
                <input
                  type="date"
                  value={blockForm.date}
                  onChange={(event) => updateBlockDate(event.target.value)}
                  className={BLOCK_INPUT_CLASS}
                />
              </label>
              <label className="min-w-0">
                <span className="mb-1 block text-[11px] font-semibold text-gray-500">Время с</span>
                <input
                  type="time"
                  value={blockForm.startTime}
                  onChange={(event) => setBlockForm({ ...blockForm, startTime: event.target.value })}
                  className={BLOCK_INPUT_CLASS}
                />
              </label>
              <label className="min-w-0">
                <span className="mb-1 block text-[11px] font-semibold text-gray-500">Время до</span>
                <input
                  type="time"
                  value={blockForm.endTime}
                  onChange={(event) => setBlockForm({ ...blockForm, endTime: event.target.value })}
                  className={BLOCK_INPUT_CLASS}
                />
              </label>
            </div>
          ) : (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <label className="min-w-0">
                <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">С</span>
                <input
                  type="date"
                  value={blockForm.date}
                  onChange={(event) => updateBlockDate(event.target.value)}
                  className={BLOCK_INPUT_CLASS}
                />
              </label>
              <label className="min-w-0">
                <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">По</span>
                <input
                  type="date"
                  value={blockForm.endDate}
                  min={blockForm.date}
                  onChange={(event) => setBlockForm({ ...blockForm, endDate: event.target.value })}
                  className={BLOCK_INPUT_CLASS}
                />
              </label>
            </div>
          )}
          <input
            value={blockForm.reason}
            onChange={(event) => setBlockForm({ ...blockForm, reason: event.target.value })}
            className="input mt-2 block w-full min-w-0 max-w-full"
            placeholder="Причина"
          />

          {blockError && (
            <p className="mt-2 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600">{blockError}</p>
          )}

          <button
            type="button"
            onClick={() => createBlockMutation.mutate()}
            disabled={createBlockMutation.isPending}
            className="mt-3 w-full rounded-xl border border-primary/20 bg-white py-2.5 text-sm font-semibold text-primary transition hover:bg-primary/5 disabled:opacity-50"
          >
            {getCreateBlockLabel(blockForm.mode, blockForm.date, blockForm.endDate, createBlockMutation.isPending)}
          </button>

          <div className="mt-4 space-y-2">
            {blocksLoading && <p className="text-xs text-gray-400">Загружаем закрытия...</p>}
            {!blocksLoading && blocks.length === 0 && (
              <p className="rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-400">Нет временно закрытых слотов</p>
            )}
            {blocks.map((block) => (
              <div
                key={block.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-700">
                    {formatBlockInterval(block, timeZone)}
                  </p>
                  {block.reason && <p className="truncate text-xs text-gray-400">{block.reason}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => deleteBlockMutation.mutate(block.id)}
                  disabled={deleteBlockMutation.isPending}
                  className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-red-500 transition hover:bg-red-50 disabled:opacity-50"
                >
                  Удалить
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SettingsSection>
  );
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(date.getDate() + days);
  return next;
}

function toDateInputValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function buildScheduleBlockInterval(form: ScheduleBlockFormState, timeZone?: string): { startsAt: Date; endsAt: Date } {
  if (form.mode === "day") {
    const startsAt = businessDateTimeToDate(form.date, "00:00", timeZone);
    const endsAt = businessDateTimeToDate(addDaysToDateKey(form.endDate || form.date, 1), "00:00", timeZone);
    return { startsAt, endsAt };
  }

  return {
    startsAt: businessDateTimeToDate(form.date, form.startTime, timeZone),
    endsAt: businessDateTimeToDate(form.date, form.endTime, timeZone),
  };
}

function addDaysToDateKey(dateKey: string, days: number): string {
  const [year, month, day] = dateKey.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return [
    String(date.getUTCFullYear()).padStart(4, "0"),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

function groupScheduleBreaks(items: WorkScheduleBreak[]): ScheduleBreakGroup[] {
  const byKey = new Map<string, ScheduleBreakGroup>();

  for (const item of items) {
    const key = `${item.startTime}|${item.endTime}|${item.label ?? ""}`;
    const group = byKey.get(key) ?? {
      key,
      ids: [],
      dayOfWeeks: [],
      startTime: item.startTime,
      endTime: item.endTime,
      label: item.label,
    };
    group.ids.push(item.id);
    group.dayOfWeeks.push(item.dayOfWeek);
    byKey.set(key, group);
  }

  return Array.from(byKey.values())
    .map((group) => ({
      ...group,
      dayOfWeeks: Array.from(new Set(group.dayOfWeeks)).sort((a, b) => a - b),
    }))
    .sort((a, b) => (a.dayOfWeeks[0] ?? 0) - (b.dayOfWeeks[0] ?? 0) || a.startTime.localeCompare(b.startTime));
}

function formatBreakDays(dayOfWeeks: number[]): string {
  const ranges: string[] = [];
  let rangeStart: number | null = null;
  let previous: number | null = null;

  for (const day of dayOfWeeks) {
    if (rangeStart === null || previous === null || day !== previous + 1) {
      if (rangeStart !== null && previous !== null) {
        ranges.push(formatDayRange(rangeStart, previous));
      }
      rangeStart = day;
    }
    previous = day;
  }

  if (rangeStart !== null && previous !== null) {
    ranges.push(formatDayRange(rangeStart, previous));
  }

  return ranges.join(", ");
}

function formatDayRange(start: number, end: number): string {
  if (start === end) return DAY_LABELS[start] ?? "";
  return `${DAY_LABELS[start] ?? ""}-${DAY_LABELS[end] ?? ""}`;
}

function getCreateBlockLabel(
  mode: ScheduleBlockMode,
  date: string,
  endDate: string,
  isPending: boolean,
): string {
  if (isPending) return "Закрываем...";
  if (mode === "day") return date === endDate ? "Закрыть день" : "Закрыть дни";
  return "Закрыть время";
}

function formatBlockInterval(block: ScheduleBlock, timeZone?: string): string {
  const start = new Date(block.startsAt);
  const end = new Date(block.endsAt);
  if (isFullDayBlock(start, end, timeZone)) {
    const lastClosedDay = new Date(end.getTime() - 60_000);
    if (toBusinessDateKey(start, timeZone) === toBusinessDateKey(lastClosedDay, timeZone)) {
      return `${formatDateWithWeekday(start, timeZone)}, весь день`;
    }
    return `${formatDateWithWeekday(start, timeZone)} - ${formatDateWithWeekday(lastClosedDay, timeZone)}, весь день`;
  }

  const startTime = formatAppointmentTime(start, timeZone);
  const endTime = formatAppointmentTime(end, timeZone);
  if (toBusinessDateKey(start, timeZone) === toBusinessDateKey(end, timeZone)) {
    return `${formatDateWithWeekday(start, timeZone)}, ${startTime}-${endTime}`;
  }

  return `${formatDateWithWeekday(start, timeZone)}, ${startTime} - ${formatDateWithWeekday(end, timeZone)}, ${endTime}`;
}

function isFullDayBlock(start: Date, end: Date, timeZone?: string): boolean {
  return end > start && isStartOfDay(start, timeZone) && isStartOfDay(end, timeZone);
}

function isStartOfDay(date: Date, timeZone?: string): boolean {
  return formatAppointmentTime(date, timeZone) === "00:00";
}

function formatDateWithWeekday(date: Date, timeZone?: string): string {
  return formatAppointmentDateWithWeekday(date, timeZone);
}

function SettingsSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-w-0 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-600">{title}</h3>
      {children}
    </div>
  );
}
